import { LocalDateTime } from 'js-joda';
import { PurchaseDetails } from 'components/business/receipts/PurchaseDetails';
import { StoreType } from '@tock/shared/utils/connect';
import { SubmitButton, Typography } from 'omakase';
import { consumer, core, requestToJSON } from '@tock/coretypes';
import { getBusinessPossessiveName } from 'utils/businessUtils';
import { getHoldAmountDue } from '@tock/shared/utils/purchase';
import { hasAnyMoniesInvolved, isPurchaseOfEvent } from 'utils/purchase';
import { isThirdPartyDeliveryVariety } from '@tock/shared/utils/offerings/varietyQueries';
import { mapPurchasedOptions } from '@tock/shared/utils/option';
import { toDateTimeString } from 'utils/timeUtils';
import Anchor from 'components/Anchor';
import ButtonGroup from '@tock/shared/components/buttons/ButtonGroup';
import CustomCancellationPolicyCard from 'components/business/checkout/CustomCancellationPolicyCard';
import ItemizedReceipt from 'components/business/receipts/ItemizedReceipt';

const wwwUrl = `https://www.exploretock.com`;
const termsUrl = `${wwwUrl}/terms`;
const privacyUrl = `${wwwUrl}/privacy`;

interface ReschedulePurchaseDetailsProps {
  app: StoreType['app'];
  business: consumer.IBusinessConfig;
  isAuthenticated: boolean;
  isSubmitting: boolean;
  onReschedule: (purchaseOrder?: core.IPurchaseOrder) => Promise<unknown>;
  onSuccess: (purchaseOrder: core.IPurchaseOrder) => void;
  purchase: core.PastPurchase;
  selectedExperience: core.TicketType;
  selection: StoreType['selection'];
  seatingAreaName?: string;
}

const ReschedulePurchaseDetails: React.FC<ReschedulePurchaseDetailsProps> = ({
  app,
  business,
  isAuthenticated,
  isSubmitting,
  onReschedule,
  onSuccess,
  purchase,
  selectedExperience,
  selection,
  seatingAreaName,
}) => {
  const options = mapPurchasedOptions(purchase);
  const partySize = purchase?.ticketCount;
  const extraChargeDetails = purchase?.ticketType?.extraChargeDetails;
  const possessiveBusinessName = getBusinessPossessiveName(business.name ?? '');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const communicationPolicy = selectedExperience.communicationPolicy!;

  const isFreeBooking =
    purchase.totalPrice === 0 && getHoldAmountDue(purchase) === 0 && purchase.subtotal === 0;

  const handleReschedule = async () => {
    const purchaseOrder = await requestToJSON<core.PurchaseOrder>(onReschedule());
    onSuccess(purchaseOrder);
  };

  return (
    <div className="Reschedule-body">
      <div className="Reschedule-body--border">
        <PurchaseDetails
          business={business}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          dateTime={LocalDateTime.parse(toDateTimeString(selection.date!, selection.time!))}
          diner={purchase.dinerPatron[0] as core.Patron}
          isAuthenticated={isAuthenticated}
          isEvent={isPurchaseOfEvent(purchase)}
          owner={purchase.ownerPatron as core.Patron}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          partySize={selection.size!}
          ticketTypeName={purchase.ticketTypeName}
          ticketType={purchase.ticketType as core.TicketType}
          seatingAreaName={seatingAreaName}
        >
          {hasAnyMoniesInvolved(purchase) && (
            <ItemizedReceipt
              alwaysShowAmountDue
              business={business}
              compAmount={purchase.compAmountCents}
              customCharge={purchase.purchasedCustomCharge as core.PurchasedCustomCharge[]}
              deliveryServiceProvider={purchase.deliveryService?.deliveryServiceProvider}
              extraChargeDetails={extraChargeDetails as core.ExtraChargeDetails}
              isGratuityForThirdPartyDelivery={isThirdPartyDeliveryVariety(purchase.ticketType)}
              isVisitEdit={purchase.originatedAt === 'VISIT_EDIT'}
              options={options}
              partySize={partySize ?? 0}
              purchase={purchase}
              purchaseFees={purchase.fee}
              showTockFeeIfRequired={true}
              title="Receipt"
              ticketType={purchase.ticketType || undefined}
            />
          )}
        </PurchaseDetails>
      </div>

      <div className="Reschedule-body--padding">
        <CustomCancellationPolicyCard
          businessName={business.name ?? ''}
          communicationPolicy={communicationPolicy}
          isFreeBooking={isFreeBooking}
          hasExternalGiftCardPayment={purchase.discount.some((disc) => disc.giftCard)}
          hasLoyaltyPointsPayment={purchase.discount.some((disc) => disc.loyaltyProgram)}
          ticketPriceInformation={purchase.ticketPriceInformation || undefined}
        />
      </div>

      <div className="Reschedule-body--padding">
        <ButtonGroup>
          <SubmitButton
            fullWidth
            isSubmitting={isSubmitting}
            size="large"
            onClick={handleReschedule}
            variant="primary"
            disabled={!!business.bookingsDisabled}
          >
            Modify reservation
          </SubmitButton>
        </ButtonGroup>
      </div>

      <div className="Reschedule-body--morePadding">
        <div className="ConsumerCheckout-acceptTerms">
          <Typography variant="caption">
            By completing your reservation, you acknowledge that you have read
            {possessiveBusinessName} cancellation policy, and agree to Tock's{' '}
            <Anchor href={termsUrl} target="_blank" className="u-activeColor">
              Terms of Use
            </Anchor>{' '}
            and{' '}
            <Anchor href={privacyUrl} target="_blank" className="u-activeColor">
              Privacy Policy.
            </Anchor>
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default ReschedulePurchaseDetails;
